import React, { useEffect, useState } from "react";
import { useApi } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { money, stageLabel, STAGE_COLORS, fmtDateTime } from "@/lib/format";
import { TrendingUp, Users, AlertTriangle, Inbox, ArrowRight, Activity } from "lucide-react";

const STAT_ICONS = {
  pipeline: TrendingUp,
  leads: Users,
  deadlines: AlertTriangle,
  approvals: Inbox,
};

function StatCard({ label, value, sub, icon: Icon, accent = "slate", testId }) {
  const colors = {
    slate: "text-slate-900",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    red: "text-red-600",
  };
  return (
    <Card data-testid={testId} className="p-5 rounded-lg border-slate-200 bg-white h-full flex flex-col justify-between hover:border-slate-300 transition-colors">
      <div className="flex items-start justify-between">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className={`h-4 w-4 ${colors[accent]}`} />
      </div>
      <div className={`font-display text-4xl sm:text-5xl font-black tracking-tighter ${colors[accent]} mt-3 font-mono`}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const { get } = useApi();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [topDeals, setTopDeals] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, a, deals] = await Promise.all([
          get("/dashboard/stats"),
          get("/dashboard/recent-activity?limit=15"),
          get("/contacts?qualification=qualified&limit=10"),
        ]);
        setStats(s.data);
        setActivity(a.data);
        setTopDeals(deals.data.items || []);
      } catch (e) { console.error(e); }
    })();
  }, []); // eslint-disable-line

  if (!stats) return <div className="text-slate-500 text-sm">Loading dashboard…</div>;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Overview</p>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter text-slate-900 mt-1">
          Pipeline Command Center
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Real-time view of every deal, deadline, and approval waiting on you.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 cw-stagger" data-testid="dashboard-stats">
        <StatCard
          label="Pipeline Value"
          value={money(stats.pipeline_value)}
          sub="Active deals (qualified → filed)"
          icon={TrendingUp}
          accent="emerald"
          testId="stat-pipeline-value"
        />
        <StatCard
          label="Qualified Leads"
          value={stats.qualified_leads}
          sub={`out of ${stats.total_leads} total`}
          icon={Users}
          testId="stat-qualified-leads"
        />
        <StatCard
          label="Deadlines < 60d"
          value={stats.deadline_alerts.length}
          sub="Leads needing prioritization"
          icon={AlertTriangle}
          accent={stats.deadline_alerts.length > 0 ? "red" : "slate"}
          testId="stat-deadlines"
        />
        <StatCard
          label="Pending Approvals"
          value={stats.pending_approvals}
          sub="Your review required"
          icon={Inbox}
          accent={stats.pending_approvals > 0 ? "amber" : "slate"}
          testId="stat-pending-approvals"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stage Funnel */}
        <Card className="lg:col-span-2 p-5 border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Pipeline by stage</p>
              <h3 className="font-display text-lg font-bold text-slate-900 mt-1">Where every deal stands</h3>
            </div>
            <Button variant="outline" size="sm" data-testid="open-pipeline-btn" onClick={() => nav("/pipeline")}>
              Open pipeline <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
          <div className="space-y-2">
            {Object.entries(stats.stage_counts).sort((a,b) => b[1] - a[1]).map(([stage, count]) => (
              <div key={stage} className="flex items-center gap-3" data-testid={`stage-bar-${stage}`}>
                <div className="w-40 shrink-0">
                  <Badge variant="outline" className={`${STAGE_COLORS[stage] || ""} text-xs font-semibold`}>
                    {stageLabel(stage)}
                  </Badge>
                </div>
                <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-slate-900 rounded-full"
                    style={{ width: `${Math.min(100, (count / Math.max(...Object.values(stats.stage_counts), 1)) * 100)}%` }} />
                </div>
                <div className="font-mono text-sm font-semibold w-10 text-right">{count}</div>
              </div>
            ))}
            {Object.keys(stats.stage_counts).length === 0 && (
              <p className="text-sm text-slate-500">No leads yet. <button className="text-slate-900 underline font-semibold" onClick={() => nav("/leads/import")}>Import your first batch</button>.</p>
            )}
          </div>
        </Card>

        {/* Deadline alerts */}
        <Card className="p-5 border-slate-200 bg-white">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Deadline radar</p>
          <h3 className="font-display text-lg font-bold text-slate-900 mt-1 mb-3">Claims expiring soon</h3>
          <div className="space-y-2">
            {stats.deadline_alerts.length === 0 && (
              <p className="text-sm text-slate-500">No imminent deadlines flagged yet. Add a <span className="font-mono">claim_deadline</span> on each contact to enable alerts.</p>
            )}
            {stats.deadline_alerts.map((d) => (
              <button
                key={d.contact_id}
                data-testid={`deadline-alert-${d.contact_id}`}
                onClick={() => nav(`/contacts/${d.contact_id}`)}
                className="w-full text-left p-2 rounded hover:bg-slate-50 border border-slate-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm text-slate-900 truncate">{d.client_name || "(unnamed)"}</div>
                  <Badge className={d.days_left < 30 ? "bg-red-100 text-red-800 border-red-200" : "bg-amber-100 text-amber-800 border-amber-200"}>
                    {d.days_left}d
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top qualified */}
        <Card className="lg:col-span-2 p-5 border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Top qualified deals</p>
              <h3 className="font-display text-lg font-bold text-slate-900 mt-1">Ready for outreach</h3>
            </div>
            <Button variant="ghost" size="sm" data-testid="open-leads-btn" onClick={() => nav("/leads")}>
              View all <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="cw-table w-full">
              <thead>
                <tr>
                  <th>Claimant</th>
                  <th>Property</th>
                  <th>County</th>
                  <th className="text-right">Surplus</th>
                  <th className="text-right">Score</th>
                </tr>
              </thead>
              <tbody>
                {topDeals.slice(0, 8).map((c) => (
                  <tr
                    key={c.id}
                    className="cursor-pointer"
                    data-testid={`deal-row-${c.id}`}
                    onClick={() => nav(`/contacts/${c.id}`)}
                  >
                    <td className="font-semibold text-slate-900">{c.client_name || "—"}</td>
                    <td className="text-slate-600">{c.property_street}, {c.property_city} {c.property_state}</td>
                    <td className="font-mono text-xs">{c.county}</td>
                    <td className="text-right font-mono font-semibold text-emerald-700">{money(c.surplus_amount)}</td>
                    <td className="text-right font-mono">{c.qualification_score ?? "—"}</td>
                  </tr>
                ))}
                {topDeals.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-slate-500 py-6">No qualified leads yet. Import a batch to begin.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Activity log */}
        <Card className="p-5 border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Activity</p>
              <h3 className="font-display text-lg font-bold text-slate-900 mt-1">Recent log</h3>
            </div>
            <Activity className="h-4 w-4 text-slate-400" />
          </div>
          <div className="relative pl-4 border-l-2 border-slate-200 space-y-3 max-h-80 overflow-auto">
            {activity.map((e) => (
              <div key={e.id} className="relative" data-testid={`audit-entry-${e.id}`}>
                <span className="absolute -left-[1.4rem] top-1 h-2.5 w-2.5 rounded-full bg-slate-900 ring-2 ring-white" />
                <div className="text-sm font-medium text-slate-900">{e.action.replace(/[_.]/g, " ")}</div>
                <div className="text-xs text-slate-500 font-mono mt-0.5">{fmtDateTime(e.timestamp)}</div>
              </div>
            ))}
            {activity.length === 0 && <div className="text-sm text-slate-500">No activity yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
